const createTheme = (breakpoints, isDarkMode) => {
  const darkModePrimaryColor = "#1e293b";
  const darkModeSecondaryColor = "#0f172a";
  const darkModeBorderColor = "#334155";
  const darkModeBackground = "#0f172a";
  const kamuBlue = "#006bd6";
  const kamuBlueHover = "#0057b3";
  const slate200 = "#e2e8f0";
  const slate50 = "#f8fafc";

  return {
    theme: {
      typography: {
        fontFamily: "'Inter', 'Roboto', sans-serif",
      },
      palette: {
        type: isDarkMode ? "dark" : "light",
        background: {
          default: isDarkMode ? darkModeBackground : "#fff",
          paper: isDarkMode ? darkModePrimaryColor : "#fff",
        },
        text: {
          primary: isDarkMode ? "#f1f5f9" : "#0f172a",
          secondary: isDarkMode ? "#94a3b8" : "#64748b",
          disabled: isDarkMode ? "#64748b" : "#94a3b8",
          hint: isDarkMode ? "#64748b" : "#94a3b8",
        },
        divider: isDarkMode ? darkModeBorderColor : slate200,
        primary: {
          light: "#3b8fe0",
          main: kamuBlue,
          dark: kamuBlueHover,
          contrastText: "#fff",
        },
        secondary: {
          light: "#33b87a",
          main: "#009a52",
          dark: "#008245",
          contrastText: "#fff",
        },
        error: {
          light: "#f87171",
          main: "#dc2626",
          dark: "#b91c1c",
          contrastText: "#fff",
        },
      },
      overrides: {
        MuiCssBaseline: {
          "@global": {
            body: {
              fontFamily: "'Inter', 'Roboto', sans-serif",
            },
          },
        },
        MuiFormLabel: {
          root: {
            "&$focused": {
              color: kamuBlue,
            },
            "&$filled": {
              color: "#64748b",
              marginTop: 4,
            },
          },
        },
        MuiFilledInput: {
          root: {
            backgroundColor: isDarkMode ? darkModeSecondaryColor : slate50,
            marginRight: 8,
            borderRadius: 12,
          },
        },
        MuiInputLabel: {
          root: {
            color: "#64748b",
            fontSize: 13,
            fontWeight: 500,
            marginLeft: 8,
          },
        },
        MuiInputAdornment: {
          positionStart: {
            marginRight: 8,
            marginBottom: 0,
          },
          positionEnd: {
            marginBottom: 0,
          },
        },
        MuiTextField: {
          root: {
            justifyContent: "center",
            width: "100%",
            paddingLeft: 12,
            paddingRight: 12,
            minHeight: 44,
            height: "auto",
            color: isDarkMode ? "#f1f5f9" : "#0f172a",
            fontSize: 13,
            fontWeight: 400,
            borderRadius: 12,
            border: "solid 1px",
            borderColor: isDarkMode ? darkModeBorderColor : slate200,
            backgroundColor: isDarkMode ? darkModeSecondaryColor : slate50,
            marginTop: 8,
            fontFamily: "'Inter', 'Roboto', sans-serif",
          },
        },
        MuiInputBase: {
          root: {
            color: isDarkMode ? "#f1f5f9" : "#0f172a",
            fontSize: 13,
            fontWeight: 400,
            borderRadius: 12,
            fontFamily: "'Inter', 'Roboto', sans-serif",
          },
        },
        MuiDialog: {
          paper: {
            borderRadius: 16,
            border: `1px solid ${isDarkMode ? darkModeBorderColor : slate200}`,
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
            backgroundColor: isDarkMode ? darkModePrimaryColor : "#fff",
            fontFamily: "'Inter', 'Roboto', sans-serif",
            [breakpoints.down("xs")]: {
              width: "100%",
              height: "100%",
              borderRadius: 0,
              margin: 0,
            },
          },
          paperScrollPaper: {
            [breakpoints.down("xs")]: {
              maxHeight: "100%",
            },
          },
        },
        MuiDialogTitle: {
          root: {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            padding: "16px 20px",
            borderBottom: `1px solid ${isDarkMode ? darkModeBorderColor : "#f1f5f9"}`,
            "& h2, & .MuiTypography-root": {
              fontWeight: 700,
              fontSize: 14,
              fontFamily: "'Inter', 'Roboto', sans-serif",
            },
          },
        },
        MuiDialogContent: {
          root: {
            padding: "16px 20px",
          },
        },
        MuiDialogActions: {
          root: {
            justifyContent: "flex-end",
            backgroundColor: isDarkMode ? darkModeSecondaryColor : slate50,
            padding: "12px 16px",
            borderTop: `1px solid ${isDarkMode ? darkModeBorderColor : slate200}`,
            gap: 8,
          },
        },
        MuiButton: {
          root: {
            backgroundColor: "transparent",
            border: "1px solid",
            borderColor: isDarkMode ? darkModeBorderColor : slate200,
            alignSelf: "center",
            width: "auto",
            height: 40,
            minWidth: 0,
            padding: "0 16px",
            borderRadius: 12,
            marginTop: 0,
            textTransform: "none",
            fontWeight: 700,
            fontSize: 12,
            fontFamily: "'Inter', 'Roboto', sans-serif",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: isDarkMode ? "#334155" : "#f1f5f9",
            },
          },
          contained: {
            boxShadow: "none",
            backgroundColor: kamuBlue,
            borderColor: kamuBlue,
            color: "#fff",
            "&:hover": {
              boxShadow: "none",
              backgroundColor: kamuBlueHover,
            },
            "&:active": {
              boxShadow: "none",
            },
          },
          containedPrimary: {
            backgroundColor: kamuBlue,
            borderColor: kamuBlue,
            color: "#fff",
            "&:hover": {
              backgroundColor: kamuBlueHover,
            },
          },
          containedSecondary: {
            backgroundColor: "#009a52",
            borderColor: "#009a52",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#008245",
            },
          },
          text: {
            border: "none",
            color: "#64748b",
            "&:hover": {
              backgroundColor: "transparent",
              color: "#0f172a",
            },
          },
        },
        MuiFab: {
          root: {
            boxShadow: "none",
          },
          primary: {
            backgroundColor: kamuBlue,
            "&:hover": {
              backgroundColor: kamuBlueHover,
            },
          },
        },
        MuiIconButton: {
          root: {
            borderRadius: 10,
          },
          colorPrimary: {
            color: kamuBlue,
          },
        },
        MuiTooltip: {
          tooltip: {
            color: "#FFFFFF",
            backgroundColor: "#0f172a",
            borderRadius: 8,
            padding: "6px 10px",
            fontSize: 11,
            fontFamily: "'Inter', 'Roboto', sans-serif",
          },
        },
        MuiAvatar: {
          colorDefault: {
            backgroundColor: isDarkMode ? darkModeSecondaryColor : slate50,
            border: "1px solid",
            borderColor: isDarkMode ? darkModeBorderColor : slate200,
            color: "#64748b",
          },
        },
        MuiTab: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            fontSize: 12,
            minHeight: 40,
            fontFamily: "'Inter', 'Roboto', sans-serif",
          },
        },
        MuiTabs: {
          indicator: {
            backgroundColor: kamuBlue,
          },
        },
        MuiMenu: {
          paper: {
            borderRadius: 12,
            border: `1px solid ${isDarkMode ? darkModeBorderColor : slate200}`,
            boxShadow: "0 10px 25px -5px rgba(0,0,0,0.15)",
          },
        },
        MuiMenuItem: {
          root: {
            fontSize: 13,
            fontFamily: "'Inter', 'Roboto', sans-serif",
            borderRadius: 8,
            margin: "2px 6px",
          },
        },
        MuiPaper: {
          root: {
            backgroundColor: isDarkMode ? darkModePrimaryColor : "#fff",
            color: isDarkMode ? "#f1f5f9" : "#0f172a",
          },
          rounded: {
            borderRadius: 16,
          },
          elevation1: {
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          },
        },
        MuiTypography: {
          colorTextPrimary: {
            color: isDarkMode ? "#f1f5f9" : "#0f172a",
          },
          colorTextSecondary: {
            color: isDarkMode ? "#94a3b8" : "#64748b",
          },
          body1: {
            color: isDarkMode ? "#f1f5f9" : undefined,
          },
          body2: {
            color: isDarkMode ? "#cbd5e1" : undefined,
          },
          h6: {
            color: isDarkMode ? "#f1f5f9" : undefined,
          },
        },
        MuiBadge: {
          colorPrimary: {
            backgroundColor: "#dc2626",
          },
        },
      },
    },
  };
};

export default createTheme;
